// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: formulaire de réunion — onglets, visibilité, validation, payload
// ═══════════════════════════════════════════════════════════════════════════
//
// Décisions pures extraites de modules/page-meeting.ts (juin 2026). Le
// monolithe garde la glu DOM/API ; ici vit la logique testable :
//
//   - TAB_ORDER / TAB_DEFAULT / readTabFromHash : onglets & lecture de l'URL.
//   - computeVisibleTabs : matrice rôle × cycle de vie (animateur/participant
//     × ouverte/fermée) qui décide quels onglets s'affichent (surface ~RBAC).
//   - resolveActiveTab : onglet actif retombant sur le 1er visible si besoin.
//   - getVisibleProjects : projets affichables selon le programme sélectionné.
//   - isMeetingSaveValid / buildMeetingPayload : garde-fou + payload POST/PUT.
//   - sanitizeMeetingPrefill : validation du pré-remplissage one-shot (données
//     non fiables venant de sessionStorage).

// ── Onglets ────────────────────────────────────────────────────────────────
export type TabKey =
  'participants' | 'actions' | 'support' | 'notes' | 'cr' | 'generation' | 'indicators';

// L'ordre détermine l'affichage de la barre d'onglets, calqué sur le parcours
// numéroté 1→6. Les guides de réunion n'y figurent pas : ils vivent dans le
// bloc Informations (cf. docs/specs/guides-reunion.md).
export const TAB_ORDER: TabKey[] = [
  'participants',
  'actions',
  'support',
  'notes',
  'cr',
  'generation',
  'indicators',
];
export const TAB_DEFAULT: TabKey = 'participants';

/** Lit l'onglet depuis le hash (`...?tab=<key>`), validé, sinon défaut. */
export function readTabFromHash(hash: string): TabKey {
  const qIdx = hash.indexOf('?');
  if (qIdx < 0) return TAB_DEFAULT;
  const params = new URLSearchParams(hash.slice(qIdx + 1));
  const raw = params.get('tab') as TabKey | null;
  return raw && TAB_ORDER.includes(raw) ? raw : TAB_DEFAULT;
}

// ── Visibilité des onglets (rôle × cycle de vie) ───────────────────────────
export interface VisibleTabsContext {
  /** Une réunion enregistrée existe (false = création). */
  hasMeeting: boolean;
  isAnimator: boolean;
  isParticipant: boolean;
  /** Réunion ouverte (opened_at non nul). */
  isOpen: boolean;
}

/**
 * Onglets visibles selon le rôle et l'état de la réunion :
 *   - création (pas de réunion) ⇒ tous (le créateur est animateur par défaut) ;
 *   - animateur ⇒ tous ;
 *   - participant non animateur ⇒ uniquement « Notes », et seulement si ouverte ;
 *   - aucun rôle ⇒ aucun onglet (bloc Informations en lecture seule).
 */
export function computeVisibleTabs(ctx: VisibleTabsContext): TabKey[] {
  if (!ctx.hasMeeting) return TAB_ORDER;
  if (ctx.isAnimator) return TAB_ORDER;
  if (ctx.isParticipant) return ctx.isOpen ? ['notes'] : [];
  return [];
}

/**
 * Onglet actif effectif : si l'onglet demandé (URL) n'est pas dans la liste
 * visible, retombe sur le premier visible. Si aucun onglet visible, conserve
 * le demandé (la barre n'est alors pas rendue).
 */
export function resolveActiveTab(requested: TabKey, visibleTabs: TabKey[]): TabKey {
  if (visibleTabs.length > 0 && !visibleTabs.includes(requested)) {
    return visibleTabs[0] as TabKey;
  }
  return requested;
}

// ── Projets visibles selon le programme ────────────────────────────────────
/**
 * Sous-ensemble des projets rattachés au programme sélectionné. Sans programme
 * (id 0) ou programme introuvable ⇒ tous les projets.
 */
export function getVisibleProjects<P extends { id: number }>(
  programId: number,
  programs: ReadonlyArray<{ id: number; projects?: ReadonlyArray<{ id: number }> }>,
  projects: ReadonlyArray<P>,
): ReadonlyArray<P> {
  if (!programId) return projects;
  const prog = programs.find((p) => p.id === programId);
  if (!prog) return projects;
  const ids = new Set((prog.projects || []).map((p) => p.id));
  return projects.filter((p) => ids.has(p.id));
}

// ── Validation & payload du save ───────────────────────────────────────────
export interface MeetingFormState {
  programId: number;
  projectId: number;
  structureId: number;
  committeeId: number;
  meetingDate: string;
  meetingTime: string;
  label: string;
  withRisks: boolean;
  participantIds: number[];
  animatorIds: number[];
}

/** État de la fiche réunion : le formulaire plus l'onglet actif. C'est
 * l'objet que `renderMeetingPage` partage avec ses onglets ; le nommer et le
 * typer est le préalable à leur extraction (point 16, 2026-09-08). */
export interface MeetingPageState extends MeetingFormState {
  activeTab: TabKey;
}

export interface MeetingSavePayload {
  project_id: number | null;
  program_id: number | null;
  structure_id: number | null;
  committee_id: number;
  meeting_date: string;
  meeting_time: string | null;
  label: string;
  with_risks: boolean;
  participant_ids: number[];
  animator_ids: number[];
}

/** Champs obligatoires avant enregistrement : comité, date, libellé non vide. */
export function isMeetingSaveValid(
  s: Pick<MeetingFormState, 'committeeId' | 'meetingDate' | 'label'>,
): boolean {
  return !!s.committeeId && !!s.meetingDate && s.label.trim().length > 0;
}

/** Assemble le payload POST/PUT (0 → null, label trimé). */
export function buildMeetingPayload(s: MeetingFormState): MeetingSavePayload {
  return {
    project_id: s.projectId || null,
    program_id: s.programId || null,
    structure_id: s.structureId || null,
    committee_id: s.committeeId,
    meeting_date: s.meetingDate,
    meeting_time: s.meetingTime || null,
    label: s.label.trim(),
    with_risks: s.withRisks,
    participant_ids: s.participantIds,
    animator_ids: s.animatorIds,
  };
}

// ── Pré-remplissage one-shot (création depuis le calendrier) ───────────────
export interface MeetingCreatePrefill {
  label?: string;
  programId?: number;
  projectId?: number;
  structureId?: number;
  committeeId?: number;
  meetingDate?: string;
  withRisks?: boolean;
  participantIds?: number[];
  animatorIds?: number[];
}

/**
 * Retient uniquement les champs du pré-remplissage dont le type est valide
 * (la donnée vient de sessionStorage, non fiable). Le résultat est destiné à
 * être fusionné dans l'état du formulaire de création.
 */
export function sanitizeMeetingPrefill(pf: MeetingCreatePrefill): Partial<MeetingFormState> {
  const out: Partial<MeetingFormState> = {};
  if (typeof pf.label === 'string') out.label = pf.label;
  if (typeof pf.programId === 'number') out.programId = pf.programId;
  if (typeof pf.projectId === 'number') out.projectId = pf.projectId;
  if (typeof pf.structureId === 'number') out.structureId = pf.structureId;
  if (typeof pf.committeeId === 'number') out.committeeId = pf.committeeId;
  if (typeof pf.meetingDate === 'string') out.meetingDate = pf.meetingDate;
  if (typeof pf.withRisks === 'boolean') out.withRisks = pf.withRisks;
  if (Array.isArray(pf.participantIds)) out.participantIds = pf.participantIds;
  if (Array.isArray(pf.animatorIds)) out.animatorIds = pf.animatorIds;
  return out;
}
